/* eslint-disable @typescript-eslint/no-empty-function */
import { insertIcons } from './icon';

export interface MobileSliderNavConfig {
    element: HTMLElement;
    itemsCount: number;
    prev: () => void;
    next: () => void;
}

const defaultConfig: Partial<MobileSliderNavConfig> = {
    itemsCount: 0,
    prev: () => {},
    next: () => {},
};

class SliderMobileNav {
    private prevBtn: HTMLButtonElement;
    private nextBtn: HTMLButtonElement;

    constructor(private config: MobileSliderNavConfig) {
        const { element, itemsCount, prev, next } = config;

        this.prevBtn = createIconButton('arrow-left', prev);
        this.nextBtn = createIconButton('arrow-right', next);

        element.appendChild(this.prevBtn);

        const bullets = Math.min(5, itemsCount);
        const bulletNodes = Array.from({ length: bullets }).map((_, i) => createBullet(i));
        bulletNodes.forEach((node) => element.appendChild(node));

        element.appendChild(this.nextBtn);

        insertIcons(element);
    }

    update(currentActive: number): void {
        this.config.element.dataset['sliderActive'] = Math.min(currentActive, 4).toString();
        this.nextBtn.disabled = currentActive === this.config.itemsCount - 1;
        this.prevBtn.disabled = currentActive === 0;
    }
}

function createBullet(number: number): HTMLSpanElement {
    const bullet = document.createElement('span');
    bullet.classList.add('slider-bullet');
    bullet.dataset['bulletNumber'] = number.toString();
    return bullet;
}

function createIconButton(icon: string, onClick?: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.classList.add('button', 'button--icon', 'has-icon-lightblue');

    const iconEl = document.createElement('span');
    iconEl.classList.add('icon');
    iconEl.dataset['icon'] = icon;
    button.appendChild(iconEl);

    if (onClick) {
        button.addEventListener('click', onClick);
    }

    return button;
}

const SLIDER_MOBILE_NAV_MAP = new WeakMap<HTMLElement, SliderMobileNav>();

export function createMobileSliderNav(config: MobileSliderNavConfig): void {
    config = Object.assign({}, defaultConfig, config);
    if (!config.element) throw new Error('Missing element when initializing mobile slider nav!');
    if (SLIDER_MOBILE_NAV_MAP.has(config.element))
        throw new Error('Tried to create slider nav for element it was already created for');

    const slider = new SliderMobileNav(config);
    SLIDER_MOBILE_NAV_MAP.set(config.element, slider);
}

export function updateMobileSliderCurrentActive(element: HTMLElement, currentActive: number): void {
    const slider = SLIDER_MOBILE_NAV_MAP.get(element);
    if (!slider) {
        console.error('Did not find slider for element', element);
        throw new Error('Did not find slider for element!');
    }

    slider.update(currentActive);
}
